import { createSlice } from '@reduxjs/toolkit';
import { endOfDay, endOfMonth, startOfDay, subDays, subMonths } from 'date-fns';
import { LOGOUT } from '../../../utils/constants';
import { dateToUTC, isFirstDayOfMonthBeforeNoonUTC } from '../../../utils/date';
import {
  fetchChatterAverageResponseTimeByTimeBuilder,
  fetchChatterAverageResponseTimeByChatterBuilder,
  fetchChatterManualVsAiByTimeBuilder,
  fetchChatterManualVsAiByChatterBuilder,
  fetchChatterPpvRateByTimeBuilder,
  fetchChatterPpvRateByChatterBuilder,
  fetchChatterTotalEarningsByChatterBuilder,
  fetchChatterTotalEarningsByTimeBuilder,
  fetchChatterTotalChatsByChatterBuilder,
  fetchChatterTotalChatsByTimeBuilder,
  fetchChatterTotalMessagesByChatterBuilder,
  fetchChatterTotalMessagesByTimeBuilder,
  fetchInfluencerEarningsByContentTypeByInfluencerBuilder,
  fetchInfluencerEarningsByContentTypeByTimeBuilder,
  fetchInfluencerEarningsByMessageTypeByInfluencerBuilder,
  fetchInfluencerEarningsByMessageTypeByTimeBuilder,
  fetchInfluencerNewUsersByTimeBuilder,
  fetchInfluencerUserInfluencersByTimeBuilder,
  fetchInfluencerUserInfluencersByInfluencerBuilder,
  fetchInfluencerTableDataBuilder,
  fetchChatterTableDataBuilder,
} from './builder';

const createInitialFetchInfo = () => ({
  pending: false,
  loaded: false,
  data: null,
  fetchedAt: null,
  requestKey: null,
  pendingRequestKey: null,
});

const INIT_STATE = {
  fetchInfluencerEarningsByContentTypeByTimeInfo: createInitialFetchInfo(),
  fetchInfluencerEarningsByContentTypeByInfluencerInfo: createInitialFetchInfo(),
  fetchInfluencerEarningsByMessageTypeByTimeInfo: createInitialFetchInfo(),
  fetchInfluencerEarningsByMessageTypeByInfluencerInfo: createInitialFetchInfo(),
  fetchInfluencerNewUsersByTimeInfo: createInitialFetchInfo(),
  fetchInfluencerUserInfluencersByTimeInfo: createInitialFetchInfo(),
  fetchInfluencerUserInfluencersByInfluencerInfo: createInitialFetchInfo(),
  fetchInfluencerTableDataInfo: createInitialFetchInfo(),
  fetchChatterTotalEarningsByTimeInfo: createInitialFetchInfo(),
  fetchChatterTotalEarningsByChatterInfo: createInitialFetchInfo(),
  fetchChatterTotalMessagesByChatterInfo: createInitialFetchInfo(),
  fetchChatterTotalMessagesByTimeInfo: createInitialFetchInfo(),
  fetchChatterTotalChatsByChatterInfo: createInitialFetchInfo(),
  fetchChatterTotalChatsByTimeInfo: createInitialFetchInfo(),
  fetchChatterAverageResponseTimeByTimeInfo: createInitialFetchInfo(),
  fetchChatterAverageResponseTimeByChatterInfo: createInitialFetchInfo(),
  fetchChatterManualVsAiByTimeInfo: createInitialFetchInfo(),
  fetchChatterManualVsAiByChatterInfo: createInitialFetchInfo(),
  fetchChatterPpvRateByTimeInfo: createInitialFetchInfo(),
  fetchChatterPpvRateByChatterInfo: createInitialFetchInfo(),
  fetchChatterTableDataInfo: createInitialFetchInfo(),
  selectedInfluencerIds: [],
  selectedChatterInfluencerId: null,
  selectedChatterIds: [],
  dateRange: isFirstDayOfMonthBeforeNoonUTC()
    ? [
        dateToUTC(subDays(endOfMonth(subMonths(new Date(), 1)), 7)).toISOString(),
        dateToUTC(endOfMonth(subMonths(new Date(), 1))).toISOString(),
      ]
    : [dateToUTC(subDays(startOfDay(new Date()), 6)).toISOString(), dateToUTC(endOfDay(new Date())).toISOString()],
};

export const organizationAnalyticsSlice = createSlice({
  name: 'organizationAnalytics',
  initialState: INIT_STATE,
  reducers: {
    setDateRange: (state, { payload }) => {
      state.dateRange = payload;
    },
    setSelectedInfluencerIds: (state, { payload }) => {
      state.selectedInfluencerIds = payload;
    },
    setSelectedChatterInfluencerId: (state, { payload }) => {
      state.selectedChatterInfluencerId = payload;
    },
    setSelectedChatterIds: (state, { payload }) => {
      state.selectedChatterIds = payload;
    },
  },
  extraReducers: builder => {
    fetchInfluencerEarningsByContentTypeByTimeBuilder(builder);
    fetchInfluencerEarningsByContentTypeByInfluencerBuilder(builder);
    fetchInfluencerEarningsByMessageTypeByTimeBuilder(builder);
    fetchInfluencerEarningsByMessageTypeByInfluencerBuilder(builder);
    fetchInfluencerNewUsersByTimeBuilder(builder);
    fetchInfluencerUserInfluencersByTimeBuilder(builder);
    fetchInfluencerUserInfluencersByInfluencerBuilder(builder);
    fetchInfluencerTableDataBuilder(builder);
    fetchChatterTotalEarningsByTimeBuilder(builder);
    fetchChatterTotalEarningsByChatterBuilder(builder);
    fetchChatterTotalMessagesByChatterBuilder(builder);
    fetchChatterTotalMessagesByTimeBuilder(builder);
    fetchChatterTotalChatsByChatterBuilder(builder);
    fetchChatterTotalChatsByTimeBuilder(builder);
    fetchChatterAverageResponseTimeByTimeBuilder(builder);
    fetchChatterAverageResponseTimeByChatterBuilder(builder);
    fetchChatterManualVsAiByTimeBuilder(builder);
    fetchChatterManualVsAiByChatterBuilder(builder);
    fetchChatterPpvRateByTimeBuilder(builder);
    fetchChatterPpvRateByChatterBuilder(builder);
    fetchChatterTableDataBuilder(builder);
    builder.addCase(LOGOUT, () => ({ ...INIT_STATE }));
  },
});

export const { setDateRange, setSelectedInfluencerIds, setSelectedChatterInfluencerId, setSelectedChatterIds } =
  organizationAnalyticsSlice.actions;

export const selectDateRange = state => state.organizationAnalyticsInfo.dateRange;
export const selectSelectedInfluencerIds = state => state.organizationAnalyticsInfo.selectedInfluencerIds;
export const selectSelectedChatterInfluencerId = state => state.organizationAnalyticsInfo.selectedChatterInfluencerId;
export const selectSelectedChatterIds = state => state.organizationAnalyticsInfo.selectedChatterIds;
export const selectFetchInfluencerEarningsByContentTypeByTimeInfo = state =>
  state.organizationAnalyticsInfo.fetchInfluencerEarningsByContentTypeByTimeInfo;
export const selectFetchInfluencerEarningsByContentTypeByInfluencerInfo = state =>
  state.organizationAnalyticsInfo.fetchInfluencerEarningsByContentTypeByInfluencerInfo;
export const selectFetchInfluencerEarningsByMessageTypeByTimeInfo = state =>
  state.organizationAnalyticsInfo.fetchInfluencerEarningsByMessageTypeByTimeInfo;
export const selectFetchInfluencerEarningsByMessageTypeByInfluencerInfo = state =>
  state.organizationAnalyticsInfo.fetchInfluencerEarningsByMessageTypeByInfluencerInfo;
export const selectFetchInfluencerNewUsersByTimeInfo = state =>
  state.organizationAnalyticsInfo.fetchInfluencerNewUsersByTimeInfo;
export const selectFetchInfluencerUserInfluencersByTimeInfo = state =>
  state.organizationAnalyticsInfo.fetchInfluencerUserInfluencersByTimeInfo;
export const selectFetchInfluencerUserInfluencersByInfluencerInfo = state =>
  state.organizationAnalyticsInfo.fetchInfluencerUserInfluencersByInfluencerInfo;
export const selectFetchInfluencerTableDataInfo = state => state.organizationAnalyticsInfo.fetchInfluencerTableDataInfo;
export const selectFetchChatterTotalEarningsByTimeInfo = state =>
  state.organizationAnalyticsInfo.fetchChatterTotalEarningsByTimeInfo;
export const selectFetchChatterTotalEarningsByChatterInfo = state =>
  state.organizationAnalyticsInfo.fetchChatterTotalEarningsByChatterInfo;
export const selectFetchChatterTotalMessagesByChatterInfo = state =>
  state.organizationAnalyticsInfo.fetchChatterTotalMessagesByChatterInfo;
export const selectFetchChatterTotalMessagesByTimeInfo = state =>
  state.organizationAnalyticsInfo.fetchChatterTotalMessagesByTimeInfo;
export const selectFetchChatterTotalChatsByChatterInfo = state =>
  state.organizationAnalyticsInfo.fetchChatterTotalChatsByChatterInfo;
export const selectFetchChatterTotalChatsByTimeInfo = state =>
  state.organizationAnalyticsInfo.fetchChatterTotalChatsByTimeInfo;
export const selectFetchChatterAverageResponseTimeByTimeInfo = state =>
  state.organizationAnalyticsInfo.fetchChatterAverageResponseTimeByTimeInfo;
export const selectFetchChatterAverageResponseTimeByChatterInfo = state =>
  state.organizationAnalyticsInfo.fetchChatterAverageResponseTimeByChatterInfo;
export const selectFetchChatterManualVsAiByTimeInfo = state =>
  state.organizationAnalyticsInfo.fetchChatterManualVsAiByTimeInfo;
export const selectFetchChatterManualVsAiByChatterInfo = state =>
  state.organizationAnalyticsInfo.fetchChatterManualVsAiByChatterInfo;
export const selectFetchChatterPpvRateByTimeInfo = state =>
  state.organizationAnalyticsInfo.fetchChatterPpvRateByTimeInfo;
export const selectFetchChatterPpvRateByChatterInfo = state =>
  state.organizationAnalyticsInfo.fetchChatterPpvRateByChatterInfo;
export const selectFetchChatterTableDataInfo = state => state.organizationAnalyticsInfo.fetchChatterTableDataInfo;

export default organizationAnalyticsSlice.reducer;
